import { DropdownMenu } from "@kobalte/core/dropdown-menu";
import { A } from "@solidjs/router";
import { StDropdown, StDropdownItem, StDropdownTrigger } from "@storytell/ui/dropdown";
import type { IconTypes } from "solid-icons";
import {
  TbArrowRight,
  TbBrain,
  TbBriefcase,
  TbChecklist,
  TbChevronDown,
  TbClockPlay,
  TbDownload,
  TbMicrophone,
  TbPlugConnected,
  TbScale,
  TbShieldCheck,
  TbSparkles,
  TbTags,
  TbTerminal2,
  TbUsersGroup,
} from "solid-icons/tb";
import { For, Show } from "solid-js";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";

type MenuItem = {
  href: string;
  label: TranslationKey;
  hint: TranslationKey;
  icon: IconTypes;
  tone: keyof typeof TONES;
};
type MenuSection = { label: TranslationKey; items: MenuItem[] };

// Light pastel tiles, one hue per item: the colour sits in the icon, not behind the column.
const TONES = {
  blue: "bg-[#e8eeff] text-[#3049cf] dark:bg-[#3049cf]/25 dark:text-[#a9b8ff]",
  violet: "bg-[#efe9ff] text-[#6d3fd6] dark:bg-[#6d3fd6]/25 dark:text-[#c4b0ff]",
  teal: "bg-[#e1f5f0] text-[#0f766e] dark:bg-[#0f766e]/30 dark:text-[#7fdcc9]",
  gold: "bg-[#fcf1d2] text-[#8c6800] dark:bg-[#c9a227]/20 dark:text-[#e8c766]",
  orange: "bg-[#ffecdc] text-[#c2410c] dark:bg-[#c2410c]/25 dark:text-[#ffb489]",
  rose: "bg-[#ffe6ec] text-[#be123c] dark:bg-[#be123c]/25 dark:text-[#ff9fb5]",
  sky: "bg-[#e0f2fe] text-[#0369a1] dark:bg-[#0369a1]/30 dark:text-[#7dd3fc]",
};

export const NAV_LINK = "text-sm font-medium text-secondary-text hover:text-primary-text transition-colors";

export const FEATURE_MENU: MenuSection[] = [
  {
    label: "marketing.nav.section_in_meeting",
    items: [
      {
        href: "/features#live-session",
        tone: "blue",
        label: "marketing.nav.live_session",
        hint: "marketing.nav.live_session_hint",
        icon: TbMicrophone,
      },
      {
        href: "/features#proactive-intelligence",
        tone: "orange",
        label: "marketing.nav.proactive",
        hint: "marketing.nav.proactive_hint",
        icon: TbSparkles,
      },
      {
        href: "/features#auto-apply-tags",
        tone: "gold",
        label: "marketing.nav.auto_tags",
        hint: "marketing.nav.auto_tags_hint",
        icon: TbTags,
      },
      {
        href: "/features#speakers",
        tone: "sky",
        label: "marketing.nav.speakers",
        hint: "marketing.nav.speakers_hint",
        icon: TbUsersGroup,
      },
      {
        href: "/features#integrations",
        tone: "violet",
        label: "marketing.nav.integrations",
        hint: "marketing.nav.integrations_hint",
        icon: TbPlugConnected,
      },
      {
        href: "/features#trust",
        tone: "teal",
        label: "marketing.nav.trust",
        hint: "marketing.nav.trust_hint",
        icon: TbShieldCheck,
      },
    ],
  },
  {
    label: "marketing.nav.section_after_meeting",
    items: [
      {
        href: "/features#after-the-meeting",
        tone: "gold",
        label: "marketing.nav.follow_through",
        hint: "marketing.nav.follow_through_hint",
        icon: TbChecklist,
      },
      {
        href: "/features#memory",
        tone: "orange",
        label: "marketing.nav.memory",
        hint: "marketing.nav.memory_hint",
        icon: TbBrain,
      },
      {
        href: "/features#automation",
        tone: "rose",
        label: "marketing.nav.automation",
        hint: "marketing.nav.automation_hint",
        icon: TbClockPlay,
      },
    ],
  },
];

export const TEAM_MENU: MenuSection[] = [
  {
    label: "marketing.nav.section_by_role",
    items: [
      {
        href: "/for-executives",
        tone: "blue",
        label: "marketing.nav.for_executives",
        hint: "marketing.nav.for_executives_hint",
        icon: TbBriefcase,
      },
      {
        href: "/for-builders",
        tone: "orange",
        label: "marketing.nav.for_builders",
        hint: "marketing.nav.for_builders_hint",
        icon: TbTerminal2,
      },
    ],
  },
  {
    label: "marketing.nav.section_compare",
    items: [
      {
        href: "/compare",
        tone: "violet",
        label: "marketing.nav.compare",
        hint: "marketing.nav.compare_hint",
        icon: TbScale,
      },
      {
        href: "/download",
        tone: "teal",
        label: "marketing.nav.download",
        hint: "marketing.nav.download_hint",
        icon: TbDownload,
      },
    ],
  },
];

const ROW = "outline-none cursor-pointer transition-colors hover:bg-panel data-highlighted:bg-panel";

const Tile = (props: { item: MenuItem; class: string; iconClass: string }) => (
  <span
    aria-hidden="true"
    class={`inline-flex shrink-0 items-center justify-center ${TONES[props.item.tone]} ${props.class}`}
  >
    <props.item.icon class={props.iconClass} />
  </span>
);

/**
 * Non-modal so opening it neither locks page scroll (the scrollbar would vanish
 * and shift the fixed header) nor blocks the neighbouring nav triggers.
 */
export const MegaMenu = (props: {
  label: string;
  sections: MenuSection[];
  more?: { href: string; label: TranslationKey };
}) => {
  const { t } = useI18n();
  return (
    <StDropdown
      modal={false}
      gutter={26}
      placement="bottom-start"
      overflowPadding={16}
      contentProps={{ class: "z-60 w-152 max-w-[calc(100vw-2rem)] rounded-2xl p-3 outline-none" }}
      content={
        <>
          <div class="grid grid-cols-2 gap-2">
            <For each={props.sections}>
              {(section) => (
                <DropdownMenu.Group class="flex flex-col">
                  <DropdownMenu.GroupLabel class="px-3 pt-2 pb-1.5 text-[11px] font-semibold tracking-wider text-muted-text uppercase">
                    {t(section.label)}
                  </DropdownMenu.GroupLabel>
                  <For each={section.items}>
                    {(item) => (
                      <StDropdownItem as={A} href={item.href} class={`flex items-start gap-3.5 rounded-xl p-3 ${ROW}`}>
                        <Tile item={item} class="size-10 rounded-[10px]" iconClass="size-5" />
                        <span class="flex min-w-0 flex-col gap-0.5">
                          <span class="text-sm font-semibold text-primary-text">{t(item.label)}</span>
                          <span class="text-[13px] leading-snug text-secondary-text">{t(item.hint)}</span>
                        </span>
                      </StDropdownItem>
                    )}
                  </For>
                </DropdownMenu.Group>
              )}
            </For>
          </div>
          <div class="mt-2 flex items-center justify-between gap-2">
            <StDropdownItem
              as={A}
              href="/changelog"
              class={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-secondary-text hover:text-primary-text data-highlighted:text-primary-text ${ROW}`}
            >
              <TbSparkles class="size-4 text-accent" aria-hidden="true" />
              {t("marketing.nav.whats_new")}
            </StDropdownItem>
            <Show when={props.more}>
              {(more) => (
                <StDropdownItem
                  as={A}
                  href={more().href}
                  class={`flex items-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-medium text-brand-text ${ROW}`}
                >
                  {t(more().label)}
                  <TbArrowRight class="size-4" aria-hidden="true" />
                </StDropdownItem>
              )}
            </Show>
          </div>
        </>
      }
    >
      <StDropdownTrigger
        class={`${NAV_LINK} group inline-flex cursor-pointer items-center gap-1 data-expanded:text-primary-text`}
      >
        {props.label}
        <TbChevronDown
          class="size-3.5 transition-transform group-data-expanded:rotate-180 motion-reduce:transition-none"
          aria-hidden="true"
        />
      </StDropdownTrigger>
    </StDropdown>
  );
};

export const MobileMenuSections = (props: { sections: MenuSection[] }) => {
  const { t } = useI18n();
  return (
    <For each={props.sections}>
      {(section) => (
        <div>
          <p aria-hidden="true" class="pb-1 text-[11px] font-semibold tracking-wider text-muted-text uppercase">
            {t(section.label)}
          </p>
          <ul aria-label={t(section.label)}>
            <For each={section.items}>
              {(item) => (
                <li>
                  <A
                    href={item.href}
                    class="group flex items-center gap-2.5 py-1.5 text-sm font-medium text-primary-text"
                  >
                    <Tile item={item} class="size-8 rounded-lg" iconClass="size-4" />
                    {t(item.label)}
                  </A>
                </li>
              )}
            </For>
          </ul>
        </div>
      )}
    </For>
  );
};
